import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";

import { ENV_CONFIG } from "../../../config";
import { apiRequest } from "../../../services/api/axios";

/**
 * Browser-native PDF viewer (iframe).
 *
 * Public URLs can be set directly on the iframe. Authenticated API PDFs are
 * fetched via apiRequest (session + CSRF) into a blob URL for cross-origin use.
 *
 * @param {Object} props - Component props.
 * @param {string} props.pdfUrl - Absolute URL to the PDF endpoint.
 * @param {boolean} [props.authenticated=false] - Fetch with credentials into a blob URL.
 * @param {string} [props.title="PDF"] - iframe title / optional heading.
 * @param {string} [props.subtitle] - Optional note shown under the heading.
 * @param {boolean} [props.showHeading=true] - Whether to show the section heading.
 * @returns {JSX.Element}
 */
const PdfViewer = ({
  pdfUrl,
  authenticated = false,
  title = "PDF",
  subtitle,
  showHeading = true,
}) => {
  const [viewerSrc, setViewerSrc] = useState(null);
  const [openTabHref, setOpenTabHref] = useState(pdfUrl);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(Boolean(authenticated));

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    /**
     * Load the PDF into iframe src (direct URL or authenticated blob).
     *
     * @returns {Promise<void>}
     */
    const load = async () => {
      setError(null);

      if (!pdfUrl) {
        setViewerSrc(null);
        setOpenTabHref(null);
        setLoading(false);
        return;
      }

      if (!authenticated) {
        setViewerSrc(`${pdfUrl}#toolbar=0`);
        setOpenTabHref(pdfUrl);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Strip API base so apiRequest can prepend it with credentials
        const apiPath = pdfUrl.startsWith(ENV_CONFIG.API_URL)
          ? pdfUrl.slice(ENV_CONFIG.API_URL.length)
          : pdfUrl;
        const blob = await apiRequest(apiPath, null, {
          method: "GET",
          responseType: "blob",
        });
        if (cancelled) return;
        if (!(blob instanceof Blob)) {
          throw new Error("PDF response was not a blob");
        }
        objectUrl = URL.createObjectURL(blob);
        setViewerSrc(`${objectUrl}#toolbar=0`);
        setOpenTabHref(objectUrl);
      } catch (err) {
        // Report even when unmounted mid-fetch so PDF failures are never silent.
        console.error("Failed to load PDF:", err);
        Sentry.captureException(err, {
          tags: { component: "PdfViewer" },
          extra: { pdfUrl },
        });
        if (!cancelled) {
          setError("Failed to load PDF. Please try again.");
          setViewerSrc(null);
          setOpenTabHref(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [pdfUrl, authenticated]);

  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-sm p-6 mb-4">
      {showHeading ? (
        <div className="mb-4 border-b pb-2">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          {subtitle ? (
            <p className="mt-1 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              {subtitle}
            </p>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-sm text-gray-500">
          Loading PDF...
        </div>
      ) : null}

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {!loading && !error && viewerSrc ? (
        <>
          <div className="bg-gray-100 border border-gray-300 rounded overflow-hidden">
            <iframe
              src={viewerSrc}
              title={title}
              className="w-full"
              style={{ border: "none", aspectRatio: "8.5 / 11" }}
            />
          </div>
          {openTabHref ? (
            <p className="text-sm text-gray-500 mt-2">
              You can also{" "}
              <a
                href={openTabHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                open the PDF in a new tab
              </a>
              .
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default PdfViewer;
